import { useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Container,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from '../common/components/LocalizationProvider';
import BaseCommandView from './components/BaseCommandView';
import PageLayout from '../common/components/PageLayout';
import SettingsMenu from './components/SettingsMenu';
import { useCatch } from '../reactHelper';
import useSettingsStyles from './common/useSettingsStyles';
import fetchOrThrow from '../common/util/fetchOrThrow';
import { getCustomCommandDataError } from '../common/util/commandValidation';

const stripAdvancedSendOptionsForNonAdmin = (command) => {
  // Remover flags avançadas caso venham de um comando "salvo" ou algo assim
  const c = { ...command };

  // Esses nomes cobrem variações comuns. Ajuste se seu backend usa outros.
  const keysToDelete = [
    'textChannel',
    'sendSms',
    'sendSMS',
    'sms',
    'enqueue',
    'queue',
    'noQueue',
    'withoutQueue',
    'skipQueue',
  ];

  keysToDelete.forEach((k) => {
    if (k in c) delete c[k];
  });

  // também remover dentro de attributes (onde normalmente fica noQueue)
  if (c.attributes && typeof c.attributes === 'object') {
    const attrs = { ...c.attributes };
    ['noQueue', 'withoutQueue', 'skipQueue'].forEach((k) => {
      if (k in attrs) delete attrs[k];
    });
    c.attributes = attrs;
  }

  return c;
};

const CommandDevicePage = () => {
  const navigate = useNavigate();
  const { classes } = useSettingsStyles();
  const t = useTranslation();

  const { id } = useParams();

  const [savedId, setSavedId] = useState(0);
  const [item, setItem] = useState({});

  const user = useSelector((state) => state.session.user);
  const isAdmin = useMemo(() => Boolean(user?.administrator), [user]);

  const customDataError = getCustomCommandDataError(item, item.textChannel, t);

  const handleSend = useCatch(async () => {
    let command;
    if (savedId) {
      const response = await fetchOrThrow(`/api/commands/${savedId}`);
      command = await response.json();
    } else {
      if (customDataError) {
        throw new Error(customDataError);
      }
      command = item;
    }

    command = { ...command, deviceId: parseInt(id, 10) };

    // Segurança: se não for admin, nunca envia flags avançadas
    if (!isAdmin) {
      command = stripAdvancedSendOptionsForNonAdmin(command);
    }

    await fetchOrThrow('/api/commands/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(command),
    });
    navigate(-1);
  });

  const validate = () => savedId || (item && item.type);

  return (
    <PageLayout menu={<SettingsMenu />} breadcrumbs={['settingsTitle', 'deviceCommand']}>
      <Container maxWidth="xs" className={classes.container}>
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              {t('sharedRequired')}
            </Typography>
          </AccordionSummary>
          <AccordionDetails className={classes.details}>
            <BaseCommandView
              deviceId={id}
              item={item}
              setItem={setItem}
              includeSaved
              savedId={savedId}
              setSavedId={setSavedId}
              // ✅ só isso: admin vê opções avançadas, user não vê
              isAdmin={isAdmin}
            />
          </AccordionDetails>
        </Accordion>

        <div className={classes.buttons}>
          <Button
            type="button"
            color="primary"
            variant="outlined"
            onClick={() => navigate(-1)}
          >
            {t('sharedCancel')}
          </Button>
          <Button
            type="button"
            color="primary"
            variant="contained"
            onClick={handleSend}
            disabled={!validate() || Boolean(customDataError)}
          >
            {t('commandSend')}
          </Button>
        </div>
      </Container>
    </PageLayout>
  );
};

export default CommandDevicePage;
